// Platform wrapper around the shared item_change hook + helpers
// (@twasta/page-runtime/txn/list). The shared versions take an injected
// executor (the package carries no HTTP client); this wrapper curries in the
// platform's `embedExecute` so every existing call site keeps its original
// signature and behavior exactly.

import { embedExecute } from './embedDataApi'
import {
  applySeededItemChangeCascades as applySeededItemChangeCascadesCore,
  applySeededProtect as applySeededProtectCore,
  applyRowEvent as applyRowEventCore,
  useItemChange as useItemChangeCore,
  type ItemChangeContext,
  type UseItemChangeOpts,
} from '@twasta/page-runtime/txn/list'
import type { ItemChangeResult } from '@twasta/page-runtime/txn'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import type { SqlModel } from '@/pages/designers/transaction/types'

export type { ItemChangeContext } from '@twasta/page-runtime/txn/list'

export async function applySeededItemChangeCascades(
  da: DataAccess | undefined,
  project: string, objName: string,
  sm: SqlModel | null,
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
  onResult?: (res: ItemChangeResult) => void,
): Promise<Record<string, unknown>> {
  return applySeededItemChangeCascadesCore(
    embedExecute, da, project, objName, sm, header, headerValues, formNo, onResult,
  )
}

export async function applySeededProtect(
  da: DataAccess | undefined,
  project: string, objName: string,
  sm: SqlModel | null,
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
): Promise<Record<string, 0 | 1>> {
  return applySeededProtectCore(
    embedExecute, da, project, objName, sm, header, headerValues, formNo,
  )
}

export async function applyRowEvent(
  da: DataAccess | undefined,
  project: string, objName: string,
  sm: SqlModel | null,
  which: 'row_add' | 'row_edit',
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
  onResult?: (res: ItemChangeResult) => void,
): Promise<Record<string, unknown>> {
  return applyRowEventCore(
    embedExecute, da, project, objName, sm, which, header, headerValues, formNo, onResult,
  )
}

export function useItemChange<T extends ItemChangeContext = ItemChangeContext>(
  opts: Omit<UseItemChangeOpts<T>, 'execute'>,
) {
  return useItemChangeCore<T>({ ...opts, execute: embedExecute })
}
